import clsx from "clsx";
import { forwardRef, memo, useEffect, useRef, useState } from "react";

import {
  VideoJsPlayerOptions,
  ThumbnailManifestType as VideoJsThumbnailManifestType,
  VideoPlayer,
  VideoPlayerHandle,
} from "@fronterahealth/frontera-ui-components";

interface VideoPlayerContentProps {
  videoUrl: string;
  thumbnailManifest?: VideoJsThumbnailManifestType | null | undefined;
  onDurationChange?: (duration: number) => void;
  onSeeking?: () => void;
  onSeeked?: () => void;
  onTimeUpdate?: (time: number) => void;
  enabledFocus?: boolean;
  onPlayerReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
  className?: string;
  isResizable?: boolean;
  initialHeight?: number;
}

export const VideoPlayerContent = memo(
  forwardRef<VideoPlayerHandle, VideoPlayerContentProps>(
    (
      {
        videoUrl,
        onDurationChange,
        thumbnailManifest,
        enabledFocus,
        onSeeked,
        onSeeking,
        onTimeUpdate,
        onPlayerReady,
        onPlay,
        onPause,
        onEnd,
        className,
        isResizable = false,
        initialHeight = 320,
      },
      ref,
    ) => {
      const wrapperRef = useRef<HTMLDivElement>(null);
      const isResizing = useRef(false);
      const [playerHeight, setPlayerHeight] = useState(initialHeight); // initial height

      // --- Resize handlers ---
      useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
          if (!isResizing.current || !wrapperRef.current) return;
          const top = wrapperRef.current.getBoundingClientRect().top;
          const newHeight = e.clientY - top;
          setPlayerHeight(newHeight > 150 ? newHeight : 150); // minimum height
        };

        const handleMouseUp = () => {
          isResizing.current = false;
          document.body.style.userSelect = "auto";
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
          window.removeEventListener("mousemove", handleMouseMove);
          window.removeEventListener("mouseup", handleMouseUp);
        };
      }, []);

      // --- Video.js options ---
      const videoPlayerOptions: VideoJsPlayerOptions = {
        sources: [{ src: videoUrl, type: "video/mp4" }],
        preload: "auto",
        fluid: false, // manual height control
        autoplay: false,
        loop: false,
        muted: false,
        playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2],
        controlBar: {
          pictureInPictureToggle: true,
          playbackRateMenuButton: true,
          children: [
            "playToggle",
            "currentTimeDisplay",
            "timeDivider",
            "durationDisplay",
            "progressControl",
            "fullscreenToggle",
            "volumePanel",
            "skipBackward",
            "skipForward",
            "playbackRateMenuButton",
            "pictureInPictureToggle",
          ],
          skipButtons: { forward: 10, backward: 10 },
        },
        inactivityTimeout: 0,
        userActions: {
          click: false,
          doubleClick: true,
        },
        html5: {
          preloadTextTracks: true,
        },
      };

      return (
        <div
          className={clsx("w-full justify-center rounded-none", className, !isResizable && "aspect-video min-h-80")}
          data-video-player="true"
        >
          <div
            ref={wrapperRef}
            className="video-sessions-player w-full relative"
            style={{
              height: isResizable ? `${playerHeight}px` : "100%",
            }}
          >
            <VideoPlayer
              ref={ref}
              src={videoUrl}
              thumbnailManifest={thumbnailManifest as VideoJsThumbnailManifestType}
              onPlay={onPlay}
              onEnded={onEnd}
              onPause={onPause}
              onSeeked={onSeeked}
              onLoadedMetadata={(duration) => {
                onDurationChange?.(duration);
              }}
              onReady={onPlayerReady}
              onSeeking={onSeeking}
              onTimeUpdate={(state) => onTimeUpdate?.(state.playedSeconds)}
              options={videoPlayerOptions}
              enableFocus={enabledFocus}
              className="w-full h-full rounded-none vjs-keep-controls vjs-user-active video-js vjs-default-skin"
            />
          </div>

          {/* Resize handle */}
          {isResizable && (
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                isResizing.current = true;
                document.body.style.userSelect = "none"; // prevent accidental text selection
              }}
              className="flex mt-1 w-full justify-center h-2 cursor-ns-resize"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="57" height="4" viewBox="0 0 57 4" fill="none">
                <path d="M1.5 2H55.5" stroke="#504D45" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </div>
          )}
        </div>
      );
    },
  ),
);
